import { make } from "vuex-pathify";
import loader from "@/util/loader";
import api from "@/util/api";
import lodashGet from "lodash/get";
import lodashIsNil from "lodash/isNil";
import forEach from "lodash/forEach";

const shortUrlModalDefaults = {
    show: false,
    hostUrl: null,
    longUrl: null,
    shortUrl: null,
    overlayUrl: null,
    title: null,
    program: null,
    disableOpenPageButton: null,
    enableCloseButton: null,
};

const initialState = {
    overlay: {
        show: false,
        url: null,
    },
    otp: {
        data: null,
        loader: loader.defaultState(),
    },
    customerDetailsLinks: {
        data: [],
        loader: loader.defaultState(),
    },
    customerCreditInfo: {
        data: {},
        loader: loader.defaultState(),
    },
    customerTags: {
        data: {},
        loader: loader.defaultState(),
    },
    quickLinks: {
        data: [],
        loader: loader.defaultState(),
    },
    financeApplications: {
        data: [],
        loader: loader.defaultState(),
    },
    activeDetailsLink: 0, // [0, ..., N-1] where N is the number of links
    messageListener: {
        shortUrlModal: {
            data: { ...shortUrlModalDefaults },
            loader: loader.defaultState(),
        },
        sendToCrmModal: {
            selectedVehicle: null,
            show: false,
        },
    },
    creditProfile: {
        data: [],
        loader: loader.defaultState(),
    },
    creditPreQualHistory: {
        data: [],
        loader: loader.defaultState(),
    },
    activityLogs: {
        data: [],
        loader: loader.defaultState(),
    },
    filteredActivityLogs: [],
    searchHistory: {
        data: [],
        loader: loader.defaultState(),
    },
    chatHistory: {
        data: null,
        loader: loader.defaultState(),
    },
    emailPreview: {
        data: null,
        loader: loader.defaultState(),
    },
};

const mutations = {
    ...make.mutations(initialState),
    RESET_SHORT_URL_MODAL_DATA: (state) => {
        state.messageListener.shortUrlModal.data = { ...shortUrlModalDefaults };
    },
    SET_SHORT_URL_MODAL_SHOW: (state, payload) => {
        state.messageListener.shortUrlModal.data.show = payload;
    },
    SET_SEND_TO_CRM_MODAL: (state, payload) => {
        state.messageListener.sendToCrmModal = payload;
    },
    SET_SHORT_URL_MODAL_DATA: (state, payload) => {
        state.messageListener.shortUrlModal.data = payload;
    },
    SET_SHORT_URL_MODAL_LOADER: (state, payload) => {
        state.messageListener.shortUrlModal.loader = payload;
    },
    SET_SHORT_URL: (state, shortUrl) => {
        state.messageListener.shortUrlModal.data.shortUrl = shortUrl;
    },
    SET_OTP_LOADER: (state, payload) => {
        state.otp.loader = payload;
    },
    SET_OTP_DATA: (state, payload) => {
        state.otp.data = payload;
    },
    SET_CUSTOMER_DETAILS_LINKS_LOADER: (state, payload) => {
        state.customerDetailsLinks.loader = payload;
    },
    SET_CUSTOMER_DETAILS_LINKS: (state, payload) => {
        state.customerDetailsLinks.data = payload;
    },
    SET_CUSTOMER_CREDIT_INFO_LOADER: (state, payload) => {
        state.customerCreditInfo.loader = payload;
    },
    SET_CUSTOMER_CREDIT_INFO: (state, payload) => {
        state.customerCreditInfo.data = payload;
    },
    SET_CREDIT_PROFILE: (state, payload) => {
        state.creditProfile.data = payload;
    },
    SET_CREDIT_PROFILE_LOADER: (state, payload) => {
        state.creditProfile.loader = payload;
    },
    SET_CREDIT_PREQUAL_HISTORY: (state, payload) => {
        state.creditPreQualHistory.data = payload;
    },
    SET_CREDIT_PREQUAL_HISTORY_LOADER: (state, payload) => {
        state.creditPreQualHistory.loader = payload;
    },
    SET_ACTIVITY_LOGS_LOADER: (state, payload) => {
        state.activityLogs.loader = payload;
    },
    SET_ACTIVITY_LOGS: (state, payload) => {
        state.activityLogs.data = payload;
    },
    SET_FILTERED_ACTIVITY_LOGS: (state, payload) => {
        state.filteredActivityLogs = payload;
    },
    SET_SEARCH_HISTORY_LOADER: (state, payload) => {
        state.searchHistory.loader = payload;
    },
    SET_SEARCH_HISTORY: (state, payload) => {
        state.searchHistory.data = payload;
    },
    SET_CHAT_HISTORY_LOADER: (state, payload) => {
        state.chatHistory.loader = payload;
    },
    SET_CHAT_HISTORY: (state, payload) => {
        state.chatHistory.data = payload;
    },
    SET_EMAIL_PREVIEW_LOADER: (state, payload) => {
        state.emailPreview.loader = payload;
    },
    SET_EMAIL_PREVIEW: (state, payload) => {
        state.emailPreview.data = payload;
    },
};

const actions = {
    openSendToCrmModal({ commit, state }, vehicle) {
        commit("SET_SEND_TO_CRM_MODAL", { selectedVehicle: vehicle, show: true });
    },
    openOverlay({ commit, state }, { iframeUrl, dealerId = null, dealerUserId = null, otpToken = null }) {
        const newIframeUrl = new URL(iframeUrl);

        if (!newIframeUrl.searchParams.has("dealerId") && dealerId) {
            newIframeUrl.searchParams.append("dealerId", dealerId);
        }

        if (!newIframeUrl.searchParams.has("dealerUserId") && dealerUserId) {
            newIframeUrl.searchParams.append("dealerUserId", dealerUserId);
        }

        if (otpToken) {
            newIframeUrl.searchParams.append("otp", otpToken);
        }

        commit("SET_OVERLAY", { show: true, url: newIframeUrl.toString() });
    },
    closeOverlay({ commit, state }) {
        commit("SET_OVERLAY", { show: false, url: null });
    },
    openShortUrlModal(
        { commit, state },
        { longUrl, overlayUrl, program, title, disableOpenPageButton, enableCloseButton }
    ) {
        let hostUrl = null;
        let shortUrl = null;

        commit("RESET_SHORT_URL_MODAL_DATA");

        try {
            hostUrl = new URL(longUrl).host;
        } catch (error) {
            commit("SET_SHORT_URL_MODAL_LOADER", loader.error(error));
            return;
        }

        commit("SET_SHORT_URL_MODAL_LOADER", loader.started());
        commit("SET_SHORT_URL_MODAL_DATA", {
            show: true,
            hostUrl,
            longUrl,
            overlayUrl,
            title,
            program,
            disableOpenPageButton,
            enableCloseButton,
        });

        api.post(`/vehicles/shortVehicleURL`, { targetUrl: longUrl })
            .then((response) => {
                shortUrl = response.data;
                commit("SET_SHORT_URL_MODAL_DATA", {
                    shortUrl,
                    ...state.messageListener.shortUrlModal.data,
                });
                commit("SET_SHORT_URL_MODAL_LOADER", loader.successful());
            })
            .catch((error) => {
                commit("SET_SHORT_URL_MODAL_LOADER", loader.error(error));
            });
    },
    fetchShortUrl({ commit, state }, longUrl) {
        commit("SET_SHORT_URL_MODAL_LOADER", loader.started());

        api.post(`/vehicles/shortVehicleURL`, { targetUrl: longUrl })
            .then((response) => {
                commit("SET_SHORT_URL", response.data);
                commit("SET_SHORT_URL_MODAL_LOADER", loader.successful());
            })
            .catch((error) => {
                commit("SET_SHORT_URL_MODAL_LOADER", loader.error(error));
            });
    },
    fetchCustomerDetailsLinks({ commit, state }, { userId, refresh = false, dealerId, programId }) {
        if (!dealerId && !programId) {
            console.error("DealerId or ProgramId is required to fetch customer details links");
            return;
        }

        if (!refresh) commit("SET_CUSTOMER_DETAILS_LINKS_LOADER", loader.started());

        const baseUrl = `/users/${userId}/details`;
        const searchParams = dealerId ? `?dealerId=${dealerId}` : `?programId=${programId}`;

        const result = api
            .get(baseUrl + searchParams)
            .then((response) => {
                commit("SET_CUSTOMER_DETAILS_LINKS", response.data);
                if (!refresh) commit("SET_CUSTOMER_DETAILS_LINKS_LOADER", loader.successful());
            })
            .catch((error) => {
                commit("SET_CUSTOMER_DETAILS_LINKS_LOADER", loader.error(error));
            });

        return result;
    },
    fetchOtp({ commit, state }, { dealerId, targetUserId }) {
        commit("SET_OTP_LOADER", loader.started());

        const result = api
            .post(`/digital-retail/logins/dealer-otps`, {
                dealerId,
                targetUserId,
            })
            .then((response) => {
                commit("SET_OTP_LOADER", loader.successful());
                commit("SET_OTP_DATA", response.data);
            })
            .catch((error) => {
                console.log("error retrieving otp");
            });

        return result;
    },
    fetchVehicle({ commit, state }, { dealerId, userId }) {
        commit("SET_STOCK_AND_VIN_FORM_LOADER", loader.started());
        let requestParams = `?dealerId=${dealerId}`;

        if (state.stockAndVinForm.stockNumber) {
            requestParams += `&stockNumber=${state.stockAndVinForm.stockNumber}`;
        }

        if (state.stockAndVinForm.vin) {
            requestParams += `&vin=${state.stockAndVinForm.vin}`;
        }

        if (userId && userId !== "") {
            requestParams += `&userId=${userId}`;
        }

        const result = api
            .get(`/vehicle/retrieve` + requestParams)
            .then((response) => {
                console.log("response =", response.data);
                commit("SET_CHANGE_VEHICLE", response.data);
                commit("SET_CHANGE_VEHICLE_SHOW", true);
                commit("SET_STOCK_AND_VIN_FORM_LOADER", loader.successful());
            })
            .catch((error) => {
                if (error.response) {
                    commit("SET_STOCK_AND_VIN_FORM_ERROR_MESSAGE", error.response.data);
                } else {
                    console.error("Error", error.message);
                }
                commit("SET_STOCK_AND_VIN_FORM_LOADER", loader.error(error));
            });

        return result;
    },
    fetchUserFinanceApplicationsV1({ commit }, { userId, dealerId }) {
        commit("SET_FINANCE_APPLICATIONS", {
            data: [],
            loader: loader.started(),
        });
        let params = {};
        if (dealerId) {
            params["dealerIds"] = dealerId;
        }
        api.get(`/users/${userId}/finance-applications`, params)
            .then((response) => {
                commit("SET_FINANCE_APPLICATIONS", {
                    data: response.data,
                    loader: loader.successful(),
                });
            })
            .catch((error) => {
                console.log(error);
                commit("SET_FINANCE_APPLICATIONS", {
                    data: [],
                    loader: loader.error(error),
                });
            });
    },
    fetchUserFinanceApplications({ commit }, { userId, dealerId, programId }) {
        commit("SET_FINANCE_APPLICATIONS", {
            data: [],
            loader: loader.started(),
        });
        let params = {};
        if (dealerId) {
            params["dealerId"] = dealerId;
        } else {
            params["programId"] = programId;
        }
        api.get(`/users/v2/${userId}/finance-applications`, params)
            .then((response) => {
                commit("SET_FINANCE_APPLICATIONS", {
                    data: response.data,
                    loader: loader.successful(),
                });
            })
            .catch((error) => {
                console.log(error);
                commit("SET_FINANCE_APPLICATIONS", {
                    data: [],
                    loader: loader.error(error),
                });
            });
    },
    fetchCustomerCreditInfoV1({ commit }, { userId, dealerId }) {
        commit("SET_CUSTOMER_CREDIT_INFO_LOADER", loader.started());
        return api
            .get(`/customers/${userId}/deal-summary?dealerId=${dealerId}`)
            .then((response) => {
                commit("SET_CUSTOMER_CREDIT_INFO", response.data);
                commit("SET_CUSTOMER_CREDIT_INFO_LOADER", loader.successful());
            })
            .catch((error) => {
                commit("SET_CUSTOMER_CREDIT_INFO_LOADER", loader.error(error));
                commit("SET_CUSTOMER_CREDIT_INFO", {});
            });
    },
    fetchCustomerCreditInfo({ commit, rootState }, { userId, dealerId }) {
        commit("SET_CUSTOMER_CREDIT_INFO_LOADER", loader.started());
        let dealerIds = [];
        if (dealerId) {
            dealerIds.push(dealerId);
        } else {
            let userDealerAccessList = lodashGet(rootState.loggedInUser, "userDealerAccessList", []);
            forEach(userDealerAccessList, (dealer) => dealerIds.push(dealer.id));
        }
        return api
            .post(`/customers/v2/${userId}/deal-summary`, { dealerIds })
            .then((response) => {
                commit("SET_CUSTOMER_CREDIT_INFO", response.data);
                commit("SET_CUSTOMER_CREDIT_INFO_LOADER", loader.successful());
            })
            .catch((error) => {
                commit("SET_CUSTOMER_CREDIT_INFO_LOADER", loader.error(error));
                commit("SET_CUSTOMER_CREDIT_INFO", {});
            });
    },
    fetchCustomerTags({ commit, rootState }, { userId, dealerId }) {
        commit("SET_CUSTOMER_TAGS", {
            data: [],
            loader: loader.started(),
        });
        let allDealerIds = [];
        if (dealerId) {
            allDealerIds.push(dealerId);
        } else {
            //send all dealer id as a comma separated string
            let userDealerAccessList = lodashGet(rootState.loggedInUser, "userDealerAccessList", []);
            forEach(userDealerAccessList, (dealer) => allDealerIds.push(dealer.id));
        }
        return api
            .post(`/customers/${userId}/tags`, allDealerIds)
            .then((response) => {
                commit("SET_CUSTOMER_TAGS", {
                    data: response.data.customerTags,
                    loader: loader.successful(),
                });
            })
            .catch((error) => {
                commit("SET_CUSTOMER_TAGS", {
                    data: [],
                    loader: loader.error(error),
                });
            });
    },
    fetchQuickLinks({ commit }, { userId, dealerId }) {
        commit("SET_QUICK_LINKS", {
            data: null,
            loader: loader.started(),
        });
        if (lodashIsNil(dealerId) || lodashIsNil(userId)) {
            commit("SET_QUICK_LINKS", {
                data: null,
                loader: loader.error("Invalid dealerId or userId"),
            });
            console.error("Invalid dealerId or userId");
            return;
        }
        let params = {};
        params["userId"] = userId;
        params["dealerId"] = dealerId;
        api.get(`/quick-links/v2`, params)
            .then((response) => {
                commit("SET_QUICK_LINKS", {
                    data: lodashGet(response, "data", null),
                    loader: loader.successful(),
                });
            })
            .catch((error) => {
                console.log(error);
                commit("SET_QUICK_LINKS", {
                    data: null,
                    loader: loader.error(error),
                });
            });
    },
    handleSetActiveDetailsLink({ commit, state }, index) {
        commit("SET_ACTIVE_DETAILS_LINK", index);
    },
    fetchActivityLogs({ commit, state }, { userId, dealerId }) {
        let params = {};
        params["userId"] = userId;
        params["dealerIds"] = dealerId;
        commit("SET_ACTIVITY_LOGS_LOADER", loader.started());
        return api
            .get(`/activity-logs`, params)
            .then((response) => {
                commit("SET_ACTIVITY_LOGS_LOADER", loader.successful());
                commit("SET_ACTIVITY_LOGS", response.data);
                //reset filter on reload
                commit("SET_FILTERED_ACTIVITY_LOGS", response.data);
            })
            .catch((error) => {
                commit("SET_ACTIVITY_LOGS_LOADER", loader.error(error));
            });
    },
    fetchCreditProfile({ commit, state }, { userId }) {
        commit("SET_CREDIT_PROFILE_LOADER", loader.started());
        return api
            .get(`/users/${userId}/credit-profile`)
            .then((response) => {
                commit("SET_CREDIT_PROFILE_LOADER", loader.successful());
                commit("SET_CREDIT_PROFILE", response.data);
            })
            .catch((error) => {
                commit("SET_CREDIT_PROFILE_LOADER", loader.error(error));
            });
    },
    fetchPreQualHistory({ commit, state }, { userId }) {
        commit("SET_CREDIT_PREQUAL_HISTORY_LOADER", loader.started());
        return api
            .get(`/users/${userId}/pre-qual`)
            .then((response) => {
                commit("SET_CREDIT_PREQUAL_HISTORY_LOADER", loader.successful());
                commit("SET_CREDIT_PREQUAL_HISTORY", response.data);
            })
            .catch((error) => {
                commit("SET_CREDIT_PREQUAL_HISTORY_LOADER", loader.error(error));
            });
    },
    fetchCustomerSearchHistory({ commit, state }, { userId, dealerId }) {
        let params = {};
        params["userId"] = userId;
        params["dealerId"] = dealerId;
        commit("SET_SEARCH_HISTORY_LOADER", loader.started());
        return api
            .get(`/vehicle-searches`, params)
            .then((response) => {
                commit("SET_SEARCH_HISTORY_LOADER", loader.successful());
                commit("SET_SEARCH_HISTORY", response.data);
            })
            .catch((error) => {
                commit("SET_SEARCH_HISTORY_LOADER", loader.error(error));
            });
    },
    fetchChatHistory({ commit, state }, request) {
        commit("SET_CHAT_HISTORY_LOADER", loader.started());
        return api
            .get(`/activity-logs/chat-content`, request)
            .then((response) => {
                commit("SET_CHAT_HISTORY_LOADER", loader.successful());
                commit("SET_CHAT_HISTORY", response.data);
            })
            .catch((error) => {
                commit("SET_CHAT_HISTORY_LOADER", loader.error(error));
            });
    },
    fetchEmailPreview({ commit }, { userId, messageId }) {
        commit("SET_EMAIL_PREVIEW_LOADER", loader.started());
        commit("SET_EMAIL_PREVIEW", null);
        return api
            .get(`/activity-logs/emails/html`, { userId, messageId })
            .then((response) => {
                commit("SET_EMAIL_PREVIEW_LOADER", loader.successful());
                commit("SET_EMAIL_PREVIEW", response.data);
            })
            .catch((error) => {
                commit("SET_EMAIL_PREVIEW_LOADER", loader.error(error));
            });
    },
    setCustomerDetailsLinks({ commit }, payload) {
        commit("SET_CUSTOMER_DETAILS_LINKS", payload);
    },
    setCustomerDetailsLinksLoader({ commit }, payload) {
        commit("SET_CUSTOMER_DETAILS_LINKS_LOADER", payload);
    },
};

const getters = {
    ...make.getters(initialState),
    isEnvironmentProd: () => {
        const environment = lodashGet(window, "_APP_CONFIG.env[0]", "local") || "local";
        return environment === "prod";
    },
    selectedDetailsLink: (state) => {
        const defaultDetailsLink = {};
        const detailsLink = lodashGet(
            state,
            `customerDetailsLinks.data[${state.activeDetailsLink}]`,
            defaultDetailsLink
        );

        return detailsLink;
    },
    isShortUrlModalOpen: (state) => {
        return state.messageListener.shortUrlModal.data.show;
    },
    activeDetailsLinkUrl: (state) => {
        const defaultUrl = "";
        const url = lodashGet(state, `customerDetailsLinks.data[${state.activeDetailsLink}].url`, defaultUrl);

        return url;
    },
};

export default {
    namespaced: true,
    state: initialState,
    actions,
    mutations,
    getters,
};
